import { fetchUserInfo, handleForcedLogout } from "@/data/slices/authSlice";
import {
  fetchNotifications,
  markNotificationAsRead,
  notificationReceived,
  notificationsSummaryChanged,
} from "@/data/slices/notificationsSlice";
import { startSignalR, clearConnection } from "@/lib/signalRClient";
import { getAccessTokenOrRefresh } from "@/lib/tokenFactory";
import { reloadEnv } from "@/lib/env";
import { notifyNotification } from "@/lib/notify";
import { executeNotificationAction } from "@/lib/notifications/notificationActions";
import i18next from "i18next";

let signalRStarted = false;

const isAuthThunkFulfilled = (action) =>
  action.type.startsWith("auth/") && action.type.endsWith("/fulfilled");

const startSignalRIfNeeded = async (store) => {
  const state = store.getState();
  if (
    state.auth.stage !== "authenticated" ||
    !state.auth.accessToken ||
    state.auth.identitySwitching ||
    state.auth.loggingOut ||
    signalRStarted
  ) {
    return;
  }

  signalRStarted = true;
  try {
    await startSignalR({
      tokenFactory: getAccessTokenOrRefresh,
      onForceLogout: () => {
        signalRStarted = false;
        store.dispatch(handleForcedLogout());
        clearConnection();
      },
      onUpdateUserInfo: async () => {
        if (store.getState().auth.loggingOut) return;

        const token = await getAccessTokenOrRefresh({ forceRefresh: true });
        if (!token || store.getState().auth.loggingOut) return;

        await store.dispatch(fetchUserInfo({ accessToken: token }));
      },
      onReconnected: async () => {
        const auth = store.getState().auth;
        if (
          auth.loggingOut ||
          auth.identitySwitching ||
          auth.stage !== "authenticated"
        ) {
          return;
        }

        const token = await getAccessTokenOrRefresh();
        const currentAuth = store.getState().auth;
        if (
          !token ||
          currentAuth.loggingOut ||
          currentAuth.identitySwitching ||
          currentAuth.stage !== "authenticated"
        ) {
          return;
        }

        await store.dispatch(fetchUserInfo({ accessToken: token }));
        await store.dispatch(fetchNotifications());
      },
      onTranslationUpdate: async () => {
        const lng = i18next.language;
        await i18next.reloadResources(lng);
        await i18next.changeLanguage(lng);
      },
      onConfigurationUpdate: async () => {
        await reloadEnv();
      },
      onNotificationCreated: async (notification) => {
        const alreadyKnown = store
          .getState()
          ?.notifications?.items?.some((item) => item.id === notification?.id);
        store.dispatch(notificationReceived(notification));
        if (alreadyKnown) return;

        notifyNotification(notification, {
          onAction: (selectedNotification) => {
            store.dispatch(executeNotificationAction(selectedNotification));
          },
          onMarkRead: (selectedNotification) => {
            store.dispatch(markNotificationAsRead(selectedNotification.id));
          },
        });
      },
      onNotificationsChanged: async (summary) => {
        store.dispatch(notificationsSummaryChanged(summary));
        await store.dispatch(fetchNotifications());
      },
      onConnectionClosed: () => {
        signalRStarted = false;
      },
    });
    await store.dispatch(fetchNotifications());
  } catch {
    signalRStarted = false;
  }
};

const signalRMiddleware = (store) => (next) => async (action) => {
  if (action.type === "auth/logout/pending") {
    signalRStarted = false;
  }

  const result = next(action);

  if (action.type === "auth/switchOrganization/fulfilled") {
    signalRStarted = false;
  }

  if (action.type === "auth/logout/rejected") {
    void startSignalRIfNeeded(store);
  }

  if (isAuthThunkFulfilled(action)) {
    await startSignalRIfNeeded(store);

    const state = store.getState();
    if (!state.auth.stage && !state.auth.accessToken && signalRStarted) {
      signalRStarted = false;
    }
  }

  return result;
};

export default signalRMiddleware;
